import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Bug, Lightbulb, Loader2, Monitor, Link2, TriangleAlert, Camera, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { captureCurrentTab } from '@/lib/screenCapture';
import type { CapturedError } from './ErrorListener';

/**
 * "Report a bug / Suggest an idea" modal. Left column: type toggle +
 * description. Right column: read-only system data (URL, resolution, an
 * optional screenshot, recent console errors from window.__errorLogs).
 * Submits everything to the submit-feedback edge function.
 *
 * Screenshots are OPT-IN via the native Screen Capture API (reliable, unlike
 * the old html2canvas auto-capture that couldn't render this app's layout).
 */
export function FeedbackModal({
  isOpen,
  onClose,
  initialScreenshot = null,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Screenshot captured (before this modal opened) of the clean page. */
  initialScreenshot?: string | null;
}) {
  const [type, setType] = useState<'bug' | 'suggestion'>('bug');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  // Manual retake. The modal is already on screen, so hide it (and the dialog
  // overlay) via a body class while grabbing the frame — otherwise the report
  // modal itself lands in the screenshot, which is exactly what we don't want.
  const handleCapture = async () => {
    setCapturing(true);
    document.body.classList.add('screenshot-capturing');
    try {
      const shot = await captureCurrentTab();
      if (shot) setScreenshotUrl(shot);
      else toast.info('No screenshot added — you can still send your report.');
    } finally {
      document.body.classList.remove('screenshot-capturing');
      setCapturing(false);
    }
  };

  // Seed the screenshot that was captured (of the clean page) before the modal
  // opened; reset when it closes.
  useEffect(() => {
    if (isOpen) {
      setScreenshotUrl(initialScreenshot);
      setCapturing(false);
    }
  }, [isOpen, initialScreenshot]);

  const errorLogs: CapturedError[] = (typeof window !== 'undefined' && window.__errorLogs) || [];
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const resolution =
    typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : '';

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Please describe the bug or idea first');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-feedback', {
        body: {
          type,
          description: `${type === 'bug' ? '[BUG]' : '[SUGGESTION]'} ${description.trim()}`,
          screenshotUrl,
          url: currentUrl,
          userAgent: navigator.userAgent,
          screenResolution: resolution,
          errorLogs,
        },
      });
      if (error || data?.success === false) throw new Error(error?.message || data?.error || 'Failed');
      toast.success(
        type === 'bug'
          ? 'Bug report sent — thank you, we’re on it!'
          : 'Idea sent — thank you for the suggestion!',
      );
      setDescription('');
      onClose();
    } catch (err) {
      console.error('Feedback submit failed:', err);
      toast.error('Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-feedback-content className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">Report a bug or suggest an idea</DialogTitle>
          <DialogDescription className="text-sm">
            Technical details and a screenshot are attached automatically. Just describe what happened.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* ── Form ── */}
          <div className="md:col-span-7 flex flex-col gap-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setType('bug')}
                className={cn(
                  'flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-[11px] border text-sm font-semibold transition-colors',
                  type === 'bug'
                    ? 'bg-destructive/10 text-destructive border-destructive/40'
                    : 'bg-card text-muted-foreground border-border hover:bg-muted/50',
                )}
              >
                <Bug className="w-4 h-4" /> Bug
              </button>
              <button
                type="button"
                onClick={() => setType('suggestion')}
                className={cn(
                  'flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-[11px] border text-sm font-semibold transition-colors',
                  type === 'suggestion'
                    ? 'bg-accent/10 text-accent border-accent/40'
                    : 'bg-card text-muted-foreground border-border hover:bg-muted/50',
                )}
              >
                <Lightbulb className="w-4 h-4" /> Idea
              </button>
            </div>

            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={7}
              className="text-sm resize-none"
              placeholder={
                type === 'bug'
                  ? 'What happened? What did you expect instead? Which steps led to it?'
                  : 'What would you like to see? How would it help your club?'
              }
            />

            <div className="flex gap-2.5 justify-end">
              <Button variant="outline" className="rounded-[11px]" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !description.trim()}
                className="rounded-[11px] bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Send feedback
              </Button>
            </div>
          </div>

          {/* ── System data ── */}
          <div className="md:col-span-5">
            <div className="rounded-[13px] border border-border bg-muted/30 p-4 flex flex-col gap-3">
              <div className="text-sm font-semibold text-muted-foreground">System data (sent automatically)</div>

              <div className="flex items-start gap-2 text-sm">
                <Link2 className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <span className="break-all text-muted-foreground">{currentUrl}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Monitor className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">{resolution}</span>
              </div>

              {screenshotUrl ? (
                <div className="relative">
                  <img
                    src={screenshotUrl}
                    alt="Screenshot preview"
                    className="rounded-[9px] border border-border max-h-40 w-full object-contain object-top bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setScreenshotUrl(null)}
                    aria-label="Remove screenshot"
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-background/90 border border-border grid place-items-center text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCapture()}
                  disabled={capturing}
                  className="rounded-[11px] justify-center"
                >
                  {capturing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                  {capturing ? 'Capturing…' : 'Add screenshot'}
                </Button>
              )}
              {!screenshotUrl && !capturing && (
                <p className="text-xs text-muted-foreground -mt-1">
                  Optional — captures the page (not this dialog). Pick this tab in the “Share this tab” prompt.
                </p>
              )}

              <div>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <TriangleAlert className="w-4 h-4 text-muted-foreground" />
                  Recent errors
                  <span
                    className={cn(
                      'text-xs font-bold px-2 py-0.5 rounded-full border',
                      errorLogs.length > 0
                        ? 'bg-warn/10 text-warn border-warn/40'
                        : 'bg-muted text-muted-foreground border-border',
                    )}
                  >
                    {errorLogs.length}
                  </span>
                </div>
                {errorLogs.length > 0 && (
                  <ul className="mt-2 max-h-28 overflow-y-auto flex flex-col gap-1">
                    {errorLogs.slice(-5).map((log, i) => (
                      <li key={i} className="text-xs text-muted-foreground truncate font-mono">
                        [{log.type}] {log.message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
